
import { WeddingApi } from '@/api/WeddingApi';
import { isMobile } from '@/utilities';
import { WeddingEventTemplate } from 'ignite360-core';
import { Component, Prop, Vue } from 'vue-property-decorator';
import vue2Dropzone from 'vue2-dropzone';

// Must match PUBLIC_UPLOAD_EXTENSIONS in the backend (BotWeddingService)
const CONTENT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  webm: 'video/webm',
};
const MAX_IMAGE_SIZE = 1000; // px, longest side
const THUMBNAIL_SIZE = 400; // px, for the gallery grid
const JPEG_QUALITY = 0.85;

const previewTemplate = `
<div class="dz-preview">
  <img data-dz-thumbnail class="dz-thumb" />
  <span class="dz-file-icon">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>
  </span>
  <span class="dz-name" data-dz-name></span>
  <div class="dz-overlay"></div>
  <div class="dz-progress"><span class="dz-upload"></span></div>
  <span class="dz-check">
    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-8 8a1 1 0 0 1-1.4 0l-4-4a1 1 0 1 1 1.4-1.4L8 12.58l7.3-7.3a1 1 0 0 1 1.4 0z" clip-rule="evenodd"/></svg>
  </span>
  <span class="dz-error-message" data-dz-errormessage></span>
</div>`;

interface S3Post {
  url: string;
  fields: Record<string, string>;
}
interface S3Upload extends S3Post {
  imageId: string;
  extension: string;
  resize: boolean;
  thumbnail?: S3Post;
}
type UploadFile = File & { s3: S3Upload; thumbnail?: Blob; previewElement: HTMLElement };

// Scales an image down to maxSize (longest side) and encodes it as JPEG.
async function toJpeg(img: HTMLImageElement, maxSize: number): Promise<Blob> {
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high'; // scaling down a phone photo is a big step
  ctx.fillStyle = '#fff'; // JPEG has no transparency
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  canvas.width = canvas.height = 0; // free the memory right away, matters on phones
  if (!blob) throw new Error('Could not encode image');
  return blob;
}

// Decodes the image once and returns the resized image and the thumbnail.
// Browsers apply the EXIF orientation when drawing an <img>, so rotated phone photos stay upright.
async function resizeImage(file: File): Promise<{ image: Blob; thumbnail: Blob }> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return {
      image: await toJpeg(img, MAX_IMAGE_SIZE),
      thumbnail: await toJpeg(img, THUMBNAIL_SIZE),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Uploads a small file with a pre-signed S3 form POST (fields first, then the file).
async function postToS3(post: S3Post, file: Blob): Promise<boolean> {
  const formData = new FormData();
  Object.entries(post.fields).forEach(([key, value]) => formData.append(key, value));
  formData.append('file', file);
  try {
    return (await fetch(post.url, { method: 'POST', body: formData })).ok;
  } catch (e) {
    return false;
  }
}

@Component({
  components: {
    vueDropzone: vue2Dropzone,
  },
})
export default class UploadPublicWeddingEventImages extends Vue {
  @Prop({ required: true, type: String })
  readonly eventId!: string;

  @Prop({ required: true, type: String })
  readonly botId!: string;

  // 'classic' keeps the plain drop area, 'celebration' shows the card with the big button
  @Prop({ required: false, type: String, default: 'classic' })
  readonly template!: WeddingEventTemplate;

  headline = 'Help capture the day';
  label = isMobile() ? 'Add photos' : 'Drop files here or click to upload';
  uploadedAny = false;
  total = 0;
  finished = 0;
  failed = 0;

  // Files go directly to S3 (pre-signed form POST), see BotWeddingService.createPublicUpload.
  // accept, url and transformFile are set in created(), they need the component instance.
  dropzoneOptions: Record<string, unknown> = {
    paramName: 'file', // S3 expects the file in "file"
    parallelUploads: 3,
    timeout: 0, // default is 30s, too short for videos on mobile networks
    maxFilesize: 200, // MB
    acceptedFiles: 'image/*,video/*',
    thumbnailWidth: 200,
    thumbnailHeight: 200,
    previewsContainer: '#wedding-upload-previews',
    previewTemplate,
    headers: {
      // dropzone defaults, S3 CORS would reject them
      'Cache-Control': null,
      'X-Requested-With': null,
    },
    dictFileTooBig: 'Larger than 200 MB',
    dictInvalidFileType: 'Not supported',
    dictResponseError: 'Upload failed',
  };

  get summary(): string {
    if (this.finished < this.total) {
      return `Uploading… ${this.finished} of ${this.total} done. Please keep this page open.`;
    }
    if (this.failed) {
      return `${this.total - this.failed} of ${this.total} uploaded, ${this.failed} failed`;
    }
    return this.total === 1 ? 'Uploaded, thank you!' : `All ${this.total} uploaded, thank you!`;
  }

  get summaryClass(): string {
    if (this.finished < this.total) return 'text-gray-600';
    return this.failed ? 'text-red-600' : 'text-green-600';
  }

  created() {
    Object.assign(this.dropzoneOptions, {
      accept: this.accept,
      url: (files: UploadFile[]) => files[0].s3.url,
      transformFile: this.transformFile,
    });
  }

  // Called by dropzone for every added file, before it is queued: get the S3 upload for it.
  async accept(file: UploadFile, done: (error?: string) => void) {
    const originalExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!CONTENT_TYPES[originalExtension]) {
      return done('Not supported');
    }
    // images are resized to JPEG in the browser, gifs (animation) and videos are uploaded as they are
    const resize =
      CONTENT_TYPES[originalExtension].startsWith('image/') && originalExtension !== 'gif';
    const extension = resize ? 'jpg' : originalExtension;
    try {
      const upload = await WeddingApi.createPublicUpload(this.botId, this.eventId, {
        extension,
        contentType: CONTENT_TYPES[extension],
        withThumbnail: resize,
      });
      file.s3 = { ...upload, extension, resize };
      done();
    } catch (e) {
      console.error('Error creating upload', e);
      done('Upload failed');
    }
  }

  transformFile(file: UploadFile, done: (file: Blob) => void) {
    if (!file.s3.resize) {
      return done(file);
    }
    resizeImage(file).then(
      ({ image, thumbnail }) => {
        file.thumbnail = thumbnail;
        done(image);
      },
      (e) => {
        // e.g. a format this browser can't decode: upload the original, without thumbnail
        console.error('Error resizing image, uploading original', e);
        done(file);
      },
    );
  }

  fileAdded() {
    // new batch after the previous one is done: reset the summary
    if (this.finished === this.total) {
      this.total = this.finished = this.failed = 0;
    }
    this.total++;
  }

  sending(file: UploadFile, xhr: XMLHttpRequest, formData: FormData) {
    // S3 policy fields must come before the file, dropzone appends the file after this event
    Object.entries(file.s3.fields).forEach(([key, value]) => formData.append(key, value));
  }

  uploadProgress(file: UploadFile, progress: number) {
    // the last 5% are for confirming the upload with our backend
    const bar = file.previewElement.querySelector('.dz-upload') as HTMLElement;
    bar.style.width = `${progress * 0.95}%`;
  }

  async success(file: UploadFile) {
    try {
      // the thumbnail is small (~30 KB) and optional, the gallery falls back to the full image
      const hasThumbnail =
        !!file.s3.thumbnail &&
        !!file.thumbnail &&
        (await postToS3(file.s3.thumbnail, file.thumbnail));
      file.thumbnail = undefined;
      await WeddingApi.completePublicUpload(this.botId, this.eventId, file.s3.imageId, {
        extension: file.s3.extension,
        hasThumbnail,
        userSessionId: this.getUserSessionId(),
      });
    } catch (e) {
      console.error('Error completing upload', e);
      (this.$refs.myDropzone as any).dropzone.emit('error', file, 'Upload failed');
      return;
    }
    file.previewElement.classList.add('dz-done');
    this.uploadedAny = true;
    this.fileFinished();
  }

  error(file: UploadFile, message: unknown, xhr?: XMLHttpRequest) {
    // errors from S3 come back as XML, show something readable instead
    if (xhr) {
      const node = file.previewElement.querySelector('[data-dz-errormessage]') as HTMLElement;
      node.textContent = 'Upload failed';
    }
    this.failed++;
    this.fileFinished();
  }

  fileFinished() {
    this.finished++;
    if (this.finished < this.total) return;

    if (this.uploadedAny) {
      this.uploadedAny = false;
      this.$emit('file-uploaded');
    }
    // the uploaded images are in the gallery now, clear the tiles after a short moment.
    // Files that failed stay, so the guest sees which ones didn't make it.
    if (!this.failed) {
      setTimeout(() => this.clearPreviews(), 1500);
    }
  }

  clearPreviews() {
    if (this.finished < this.total) return; // a new batch started in the meantime
    (this.$refs.myDropzone as any)?.dropzone?.removeAllFiles();
    this.total = this.finished = this.failed = 0;
  }

  // used by the page for the sticky button and the "add more" card
  openFilePicker() {
    (this.$refs.myDropzone as any)?.dropzone?.hiddenFileInput?.click();
  }

  getUserSessionId(): string {
    let userSessionId = localStorage.getItem('userSessionId');
    if (!userSessionId) {
      userSessionId = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('userSessionId', userSessionId);
    }
    return userSessionId;
  }
}
